import {
  Alert,
  Button,
  Group,
  Modal,
  MultiSelect,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useOrgScopedQuery } from "../../query/scope";
import {
  getRepoHostTokenGuide,
  updateRepoHostToken,
  type RepoHostTokenSummary,
  type UpdateTokenRequest,
} from "./tokensApi";

// Доступные scope-опции для MultiSelect.
const SCOPE_OPTIONS = [
  "read:repository",
  "write:repository",
  "read:issue",
  "write:issue",
];

export interface TokenUpdateModalProps {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly role: string;
  readonly existing?: RepoHostTokenSummary;
  readonly onSuccess: () => void;
}

interface TokenUpdateBodyProps {
  readonly role: string;
  readonly existing: RepoHostTokenSummary | undefined;
  readonly onSuccess: () => void;
  readonly onClose: () => void;
}

// Тело формы — монтируется только когда модалка открыта (сброс state).
function TokenUpdateModalBody(props: TokenUpdateBodyProps): ReactElement {
  const { t } = useTranslation();
  const { role, existing, onSuccess, onClose } = props;
  const queryClient = useQueryClient();

  const [tokenValue, setTokenValue] = useState("");
  const [tokenName, setTokenName] = useState(existing?.token_name ?? "");
  const [scopes, setScopes] = useState<string[]>(
    existing !== undefined ? [...existing.declared_scopes] : [],
  );
  const [formError, setFormError] = useState<string | null>(null);

  // required_scopes роли — least-privilege чек-лист (тот же источник, что
  // ручная инструкция); сверяем выбранные scope'ы против него.
  const guideQuery = useOrgScopedQuery({
    key: ["connections", "forgejo", "guide", role],
    queryFn: () => getRepoHostTokenGuide(role),
    retry: false,
  });
  const missingScopes = (guideQuery.data?.required_scopes ?? []).filter(
    (scope) => !scopes.includes(scope),
  );

  const mutation = useMutation<RepoHostTokenSummary, Error, UpdateTokenRequest>(
    {
      mutationFn: (body) => updateRepoHostToken(role, body),
      onSuccess: () => {
        setFormError(null);
        void queryClient.invalidateQueries({
          queryKey: ["connections", "forgejo", "tokens"],
        });
        onSuccess();
        onClose();
      },
      onError: (err) => {
        setFormError(t("connections.genericError", { message: err.message }));
      },
    },
  );

  const handleSubmit = (): void => {
    if (tokenValue.trim() === "") {
      setFormError(
        `${t("connections.tokens.updateModal.tokenValueLabel")} обязательно`,
      );
      return;
    }
    setFormError(null);
    mutation.mutate({
      token_value: tokenValue,
      token_name: tokenName !== "" ? tokenName : null,
      declared_scopes: scopes,
    });
  };

  return (
    <Stack gap="sm">
      {formError !== null ? (
        <Alert color="red" title={t("connections.errorHeading")}>
          {formError}
        </Alert>
      ) : null}

      <PasswordInput
        label={t("connections.tokens.updateModal.tokenValueLabel")}
        placeholder={t("connections.tokens.updateModal.tokenValuePlaceholder")}
        value={tokenValue}
        onChange={(e) => {
          setTokenValue(e.currentTarget.value);
        }}
        required
      />

      <Text size="xs" c="dimmed">
        {t("connections.tokens.updateModal.usernameAutoNote")}
      </Text>

      <TextInput
        label={t("connections.tokens.updateModal.tokenNameLabel")}
        placeholder={t("connections.tokens.updateModal.tokenNamePlaceholder", {
          role,
        })}
        value={tokenName}
        onChange={(e) => {
          setTokenName(e.currentTarget.value);
        }}
      />

      <MultiSelect
        label={t("connections.tokens.updateModal.scopesLabel")}
        data={SCOPE_OPTIONS}
        value={scopes}
        onChange={setScopes}
      />

      {missingScopes.length > 0 ? (
        <Alert color="yellow">
          {t("connections.tokens.updateModal.scopeWarning", {
            scopes: missingScopes.join(", "),
          })}
        </Alert>
      ) : null}

      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          {t("connections.modalCancel")}
        </Button>
        <Button onClick={handleSubmit} loading={mutation.isPending}>
          {t("connections.tokens.updateModal.saveButton")}
        </Button>
      </Group>
    </Stack>
  );
}

// Модалка PUT /tokens/{role} — добавление или замена токена роли.
export function TokenUpdateModal(props: TokenUpdateModalProps): ReactElement {
  const { t } = useTranslation();
  const { opened, onClose, role, existing, onSuccess } = props;

  const isAdd = existing === undefined;
  const title = isAdd
    ? t("connections.tokens.updateModal.titleAdd", { role })
    : t("connections.tokens.updateModal.titleReplace", { role });

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="md">
      {opened ? (
        <TokenUpdateModalBody
          role={role}
          existing={existing}
          onSuccess={onSuccess}
          onClose={onClose}
        />
      ) : null}
    </Modal>
  );
}
